import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import Button from "../ui/Button/Button";
import PageSectionCard from "../ui/Page/PageSectionCard";
import { guardianStatusPillClassName, scrollablePanelClassName } from "./constants";
import type { GuardianConnectionCardItem } from "./types";

type GuardianConnectionsPanelProps = {
  title: string;
  subtitle: string;
  emptyMessage: string;
  items: GuardianConnectionCardItem[];
  footer?: ReactNode;
};

const GuardianConnectionsPanel = ({ title, subtitle, emptyMessage, items, footer }: GuardianConnectionsPanelProps) => {
  return (
    <PageSectionCard className="sm:rounded-[2rem]">
      <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:items-start sm:justify-between sm:text-left">
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-myteengig-text-muted">{title}</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-myteengig-text-primary">{subtitle}</h2>
        </div>
        <span className="rounded-full bg-myteengig-background px-3 py-1 text-sm font-medium text-myteengig-text-primary">
          {items.length} {items.length === 1 ? "connection" : "connections"}
        </span>
      </div>

      {items.length === 0 ? (
        <div className="mt-6 rounded-2xl border border-dashed border-myteengig-border bg-myteengig-background px-5 py-6 text-sm text-myteengig-text-muted">
          {emptyMessage}
        </div>
      ) : (
        <div className={scrollablePanelClassName(items.length)}>
          {items.map((item) => {
            const action = item.action;

            return (
              <article
                key={item.id}
                className="rounded-[1.5rem] border border-myteengig-border bg-[linear-gradient(180deg,_rgba(255,255,255,1),_rgba(234,248,255,0.7))] p-5"
              >
                <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:text-left">
                  <div className="min-w-0">
                    <p className="text-lg font-semibold text-myteengig-text-primary">{item.name}</p>
                    <p className="mt-1 text-sm text-myteengig-text-muted">{item.email}</p>
                    {item.meta ? <p className="mt-3 text-xs uppercase tracking-[0.18em] text-myteengig-text-muted">{item.meta}</p> : null}
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
                    {item.attentionLabel ? (
                      <span className="inline-flex items-center gap-2 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-900">
                        <span className="h-2.5 w-2.5 rounded-full bg-amber-500" />
                        {item.attentionLabel}
                        {typeof item.attentionCount === "number" && item.attentionCount > 0 ? ` · ${item.attentionCount}` : ""}
                      </span>
                    ) : null}
                    <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${guardianStatusPillClassName.ACTIVE}`}>
                      {item.statusLabel}
                    </span>
                  </div>
                </div>

                {action ? (
                  <div className="mt-5 flex justify-center sm:justify-start">
                    {action.href ? (
                      <Link to={action.href}>
                        <Button variant={action.variant ?? "outline"}>{action.label}</Button>
                      </Link>
                    ) : (
                      <Button
                        variant={action.variant ?? "outline"}
                        disabled={action.disabled || action.pending}
                        onClick={action.onClick}
                      >
                        {action.pending ? "Working..." : action.label}
                      </Button>
                    )}
                  </div>
                ) : null}
              </article>
            );
          })}
        </div>
      )}

      {footer ? <div className="mt-6">{footer}</div> : null}
    </PageSectionCard>
  );
};

export default GuardianConnectionsPanel;
